<template>
  <div class="nav-bg">
    <div class="nav-box">
      <ul
        class="nav-list flex justify-center"
        :class="{
          'is-nav-collapsed': navCollapsed,
          'is-switching-tab': switchingTab,
        }"
        @mouseenter="enter"
        @mouseleave="out"
      >
        <li
          v-for="item in nav"
          :key="item.id"
          :class="{ 'is-sub-pinned': pinnedNavId === String(item.id) }"
          @mouseenter="enterNav(item)"
          @pointerdown="onNavLiPointerDown($event, item)"
        >
          <template v-if="item.link && item.link != '#'">
            <a
              v-if="item.childrenList.length > 0"
              :class="[{ active: activeNav === item.link, navHit: activeNavId[0] && item.id === activeNavId[0] }, 'nav-link-item']"
            >{{ item.name }}</a>
            <nuxt-link
              v-else
              @click="onLinkClick(item)"
              :to="localePath(item.link)"
              :target="item.link.includes('http') ? '_blank' : ''"
              :class="[{ active: activeNav === item.link, navHit: activeNavId[0] && item.id === activeNavId[0] }, 'nav-link-item']"
              :rel="item.link.includes('http') ? 'nofollow noopener noreferrer' : ''"
            >{{ item.name }}</nuxt-link>

            <div
              class="nav-sub"
              v-if="item.childrenList.length > 0"
            >

              <div class="nav-sub-box-wrap">
                <div class="nav-sub-box-title">
                  {{ item.name }}
                </div>
                <div class="nav-sub-box-content">
                  <!-- 左侧文案和图片展示内容部分 -->
                  <div class="nav-sub-content">
                    <div class="nav-sub-card-img">
                      <img
                        :src="item.image"
                        alt=""
                      >
                    </div>
                    <div class="nav-sub-content-text">

                      {{ item.text }}

                    </div>
                  </div>
                  <div class="nav-split-line-content">
                    <div class="nav-split-line-item"></div>
                  </div>
                  <!-- 右侧列表展示部分 -->
                  <ul class="sub-list">
                    <li
                      v-for="(value, childIndex) in item.childrenList"
                      :key="value.id"
                      :class="{ subNavHit: activeNavId[1] && value.id === activeNavId[1] }"
                      @mouseenter="subIndex = childIndex"
                    >

                      <!-- <div
                      class="sub-con flex-center"
                      v-if="childIndex == subIndex"
                    >
                      <div class="sub-des MultiLine">
                        <div
                          class="text"
                          v-html="value.text"
                        ></div>
                      </div>
                    </div> -->
                      <nuxt-link
                        v-if="value.link != '#'"
                        @click="onLinkClick(value)"
                        :to="localePath(value.link)"
                        :target="value.link.includes('http') ? '_blank' : ''"
                        :rel="value.link.includes('http') ? 'nofollow noopener noreferrer' : ''"
                      >{{ value.name }}</nuxt-link>
                      <a
                        v-else
                        @click="onShowMsg"
                      >{{ value.name }}</a>

                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <a @click="onShowMsg">{{ item.name }}</a>
            <div
              class="nav-sub"
              v-if="item.childrenList.length > 0"
            >
              <template
                v-for="value in item.childrenList"
                :key="value.id"
              >
                <nuxt-link
                  v-if="value.link != '#'"
                  @click="onLinkClick(value)"
                  :to="localePath(value.link)"
                  :target="value.link.includes('http') ? '_blank' : ''"
                  :rel="value.link.includes('http') ? 'nofollow noopener noreferrer' : ''"
                >{{ value.name }}</nuxt-link>
                <a
                  v-else
                  @click="onShowMsg"
                >{{ item.name }}</a>
              </template>
            </div>
          </template>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onUnmounted, ref, watch } from 'vue'
import { useLocalePath, useRoute } from '#imports'
import type { HeaderNavItem } from '~/types'

defineProps<{
  nav: HeaderNavItem[]
  activeNav: string
  activeNavId: string[]
}>()

const emit = defineEmits<{
  'nav-hover-change': [expanded: boolean]
  'link-click': [item: HeaderNavItem]
  'show-msg': []
}>()

const localePath = useLocalePath()
const route = useRoute()

const subIndex = ref(0)
/** 点击跳转或路由变化后强制收起，避免鼠标仍悬停时 :hover 保持展开 */
const navCollapsed = ref(false)
/** 下拉是否已真正展开：已展开时切换 Tab / 点击不再走收起再展开 */
const navOpen = ref(false)
/** 下拉展开后切换一级 Tab 时只替换内容，不重复播放展开/收起动画 */
const switchingTab = ref(false)
/** 当前钉住展开的一级项：避免切 Tab 时 :hover 先丢失 + is-switching-tab 导致瞬间收起 */
const pinnedNavId = ref<string | null>(null)

/** 快速划过不展开：稳定悬停后再打开 */
const HOVER_INTENT_DELAY_MS = 120
let hoverIntentTimer: number | null = null
let hoverIntentTargetId: string | null = null

const clearHoverIntent = () => {
  if (hoverIntentTimer != null) {
    window.clearTimeout(hoverIntentTimer)
    hoverIntentTimer = null
  }
  hoverIntentTargetId = null
}

const openNav = (itemId?: string) => {
  navCollapsed.value = false
  navOpen.value = true
  if (itemId != null) pinnedNavId.value = itemId
  emit('nav-hover-change', true)
}

const collapseNav = () => {
  clearHoverIntent()
  switchingTab.value = false
  pinnedNavId.value = null
  navCollapsed.value = true
  navOpen.value = false
  emit('nav-hover-change', false)
  subIndex.value = 0
}

/** 触摸/折叠屏再次点击时解锁 hover 展开（mouseleave 不一定触发） */
const unlockNav = (itemId?: string) => {
  clearHoverIntent()
  switchingTab.value = false
  openNav(itemId)
  subIndex.value = 0
}

const enter = () => {
  clearHoverIntent()
  // 从外部进入时先压住 CSS :hover，避免快速划过瞬间弹出
  // 已展开时不收起，避免点击/移回一级时闪烁
  if (!navOpen.value) {
    navCollapsed.value = true
    switchingTab.value = false
    pinnedNavId.value = null
    emit('nav-hover-change', false)
  }
}

const out = () => {
  clearHoverIntent()
  switchingTab.value = false
  pinnedNavId.value = null
  navCollapsed.value = false
  navOpen.value = false
  emit('nav-hover-change', false)
  subIndex.value = 0
}

const enterNav = (item: HeaderNavItem) => {
  const hasChildren = !!item.childrenList?.length
  const itemId = String(item.id)

  if (!hasChildren) {
    clearHoverIntent()
    // 先恢复 transition，再取消钉住 → 与 mouseleave 一样走 0.28s 收回
    switchingTab.value = false
    if (navOpen.value) {
      navOpen.value = false
      pinnedNavId.value = null
      emit('nav-hover-change', false)
    } else {
      navCollapsed.value = true
      pinnedNavId.value = null
      emit('nav-hover-change', false)
    }
    return
  }

  // 已展开：只切内容，不收回、不重播动画
  if (navOpen.value) {
    clearHoverIntent()
    switchingTab.value = true
    navCollapsed.value = false
    pinnedNavId.value = itemId
    subIndex.value = 0
    return
  }

  // 未展开：延迟打开，快速划过取消
  switchingTab.value = false
  navCollapsed.value = true
  pinnedNavId.value = null
  emit('nav-hover-change', false)
  subIndex.value = 0

  clearHoverIntent()
  hoverIntentTargetId = itemId
  hoverIntentTimer = window.setTimeout(() => {
    if (hoverIntentTargetId !== itemId) return
    switchingTab.value = false
    openNav(itemId)
  }, HOVER_INTENT_DELAY_MS)
}

/** 收起后再次点击一级入口解锁；忽略子菜单内 pointerdown，避免跳转前误展开 */
const onNavLiPointerDown = (e: PointerEvent, item: HeaderNavItem) => {
  const hasChildren = !!item.childrenList?.length
  if (!hasChildren || !navCollapsed.value) return
  if ((e.target as HTMLElement).closest('.nav-sub')) return
  unlockNav(String(item.id))
}

const onLinkClick = (item: HeaderNavItem) => {
  collapseNav()
  emit('link-click', item)
}
const onShowMsg = () => {
  collapseNav()
  emit('show-msg')
}

watch(
  () => route.path,
  () => {
    collapseNav()
  },
)

onUnmounted(() => {
  clearHoverIntent()
})
</script>

<style scoped lang="scss">
.nav-bg {
  height: 64px;

  .nav-list {
    list-style: none;
    display: inline-flex;

    >li {
      padding: 0 21px;
      transition: all 0.3s;

      .nav-link-item {
        position: relative;
        display: block;
        font-size: 16px;
        color: #ffffff;
        line-height: 64px;

        &::before {
          // display: block;
          // content: '';
          // position: absolute;
          // left: 0%;
          // bottom: 0;
          // width: 100%;
          // height: 2px;
          transform: scaleX(0);
          background-color: var(--org-color);
          transition: transform 0.3s;
        }

        &.navHit {
          color: var(--org-color);

          &::before {
            transform: scaleX(1);
          }
        }

        &.active {
          // color: var(--org-color);
        }
      }

      // .nav-sub {
      //     opacity: 0.5;
      //     visibility: visible;
      //     transform: scaleY(1);
      //   }
    }

    &:not(.is-nav-collapsed) > li:hover,
    &:not(.is-nav-collapsed) > li.is-sub-pinned {
      > a {
        // font-weight: bold; // 不需要加粗
        color: var(--org-color);

        &::before {
          transform: scaleX(1);
        }
      }

      .nav-sub {
        opacity: 1;
        visibility: visible;
        transform: scaleY(1);
        pointer-events: auto;
        transition-delay: 0s;
      }
    }

    .nav-sub-content-text {
      color: rgba(255, 255, 255, 0.60);
      font-feature-settings: 'liga' off, 'clig' off;
      font-family: "PingFang SC";
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 27px;
      width: 355px;
      margin-top: 24px;
      /* 168.75% */
    }

    .nav-sub {
      position: absolute;
      left: 0;
      top: var(--header-height, 64px);
      width: 100%;
      opacity: 0;
      visibility: hidden;
      transform: scaleY(0);
      transform-origin: center top;
      // 必须在页面内容之上，否则二级链接点不到
      z-index: 1;
      pointer-events: none;
      transition:
        opacity 0.28s ease,
        transform 0.28s cubic-bezier(0.22, 1, 0.36, 1),
        visibility 0s linear 0.28s;
      will-change: opacity, transform;

      // Figma 969:5854：纯 #151b29 实底（与 SVG fill 一致）
      &::before {
        display: block;
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        min-height: var(--nav-dropdown-panel-min-height, 399px);
        background-color: var(--nav-dropdown-panel-bg);
        box-shadow: var(--nav-dropdown-panel-shadow);
        z-index: -1;
      }

      a {
        display: block;
        font-size: 14px;
        color: #0d131a;
        line-height: 26px;
        // padding: 5px 15px;
        transition: all 0.3s;
      }
    }

    &.is-switching-tab .nav-sub {
      transition: none;
    }

    .sub-list {
      list-style: none;

      li {
        transition: all 0.3s;
        margin-bottom: 24px;

        &:last-child {
          margin-bottom: 0;
        }

        a {
          display: block;
          max-width: 242px;
          color: #FFF;
          font-feature-settings: 'liga' off, 'clig' off;
          font-family: "PingFang SC";
          font-size: 16px;
          font-style: normal;
          font-weight: 400;
          line-height: 27px;
          /* 168.75% */
          transition: all 0.3s;
        }

        &.active,
        &:hover {

          // background-color: rgba($color: #242c3e, $alpha: 0.5);
          a {
            color: var(--org-color);
            // background-image: url('@/assets/images/icon/icon-nav.webp');
          }
        }

        &.subNavHit:not(.active) {
          a {
            color: var(--org-color);
          }
        }
      }

      .sub-con {
        position: absolute;
        left: 0;
        top: 0;
        width: 50%;
        height: 100%;

        min-height: 186px;
        align-items: flex-start;
        font-size: 16px;
        color: #ffffff;
        line-height: 26px;
        padding: 28px 24px 38px;
        background-color: rgba($color: #242c3e, $alpha: 0.5);

        .sub-des {
          max-width: 721px;
        }
      }
    }
  }
}

.nav-sub-box-wrap {
  width: 100%;
  min-height: 400px;
  max-width: 1320px;
  padding: 40px 15px;
  margin: 0 auto;
  // display: flex;
  position: relative;

  .nav-sub-box-title {
    color: #FFF;
    font-feature-settings: 'liga' off, 'clig' off;
    /* 16px/medium */
    font-family: "PingFang SC";
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    margin-bottom: 24px;
  }

  .nav-sub-box-content {
    display: flex;
    justify-content: flex-start;

    .nav-split-line-content {
      padding: 0 88px;

      .nav-split-line-item {
        width: 1px;
        height: 100%;
        background-color: rgba($color: #ffffff, $alpha: 0.06);
        // height: 273px;
      }
    }


    .nav-sub-card-img {
      width: 356px;
      height: 195px;
      border-radius: 12px;
      background: var(--bg-color-3);
      overflow: hidden;

      img {
        border-radius: 12px;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      img[src=""], img:not([src]) {
        opacity: 0;
      }
    }
  }


  // 分割线


}


@media (max-width: 1600px) {
  .nav-bg .nav-list .sub-list li a {
    // font-size: 18px;
    // line-height: 24px;
    // background-size: 18px auto;
  }
}

@media (max-width: 1400px) {
  .nav-bg .nav-list {
    >li {
      padding: 0 12px;

      >a {
        font-size: 15px;
      }
    }

    .sub-list li a {
      font-size: 16px;
      // padding: 10px 20px;
    }
  }
}

@media (max-width: 911px) {
  .nav-bg {
    display: none;
  }
}
</style>
